import React, {useEffect, useRef, useState} from 'react';
import './SimilarMessages.styles.css';
import {Box, IconButton, Menu, Tooltip} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import {GridColDef, GridToolbarColumnsButton} from '@mui/x-data-grid';
import moment from 'moment';
import {SimilarMessagesProps, SimilarMessagesState} from './SimilarMessages.props';

import {getSimilarMessages, updateTriageInboxes} from '../../../api/emailTriage';
import {DataTable} from '../../molecules/DataTable';
import {TableDropdown} from '../../molecules/TableDropdown';
import {DropdownContent} from '../../molecules/DropDownContent';
import {DropDownList} from '../../atoms/DropDownList';
import {Pagination} from '../../atoms/Pagination';
import {DateRangePicker} from '../../atoms/DateRangePicker';
import {RadialGraph} from '../../atoms/RadialGraph';
import {Toast} from '../../atoms/Toast';
import {SearchMenuBar} from '../SearchMenuBar';
import {BulkActionModal} from '../BulkActionModal';
import {BulkActionChanges} from '../BulkActionModal/BulkActionModal.props';
import {getBulkActionOutcome} from '../../../utils/bulkAction';
import {getTimeZoneName} from '../../../utils/common';
import {priorityDropdown, statusDropdown} from '../../../utils/inboxStyles';
import {getSimilarWindow} from '../../../utils/similarWindow';
import {BULK_ACTION} from '../../../constants/contants';

type SimilarMessagesContentProps = Omit<SimilarMessagesProps, 'state' | 'setState'> & {
  state: SimilarMessagesState;
  setState: React.Dispatch<React.SetStateAction<SimilarMessagesState>>;
};

const BULK_ACTION_MENU = {BulkAction: BULK_ACTION};
const SIMILARITY_OPTIONS = [
  {id: 1, content: 'Same Sender', key: 'same_sender'},
  {id: 2, content: 'Same Subject', key: 'same_subject'},
  {id: 3, content: 'Same Sender & Subject', key: 'same_sender_subject'},
];
const SIMILARITY_LABELS: Record<string, string> = {
  current: 'CURRENT MESSAGE',
  ...Object.fromEntries(SIMILARITY_OPTIONS.map(({key, content}) => [key, content])),
};
const STATUS_OPTIONS = [{id: 1, content: 'Received'}, {id: 2, content: 'In Review'}, {id: 3, content: 'Resolved'}];
const PRIORITY_OPTIONS = [{id: 1, content: 'High'}, {id: 2, content: 'Medium'}, {id: 3, content: 'Low'}, {id: 4, content: 'Unknown'}];
const CATEGORY_OPTIONS = [{id: 1, content: 'Threat'}, {id: 2, content: 'Clean'}, {id: 3, content: 'Unknown'}];
const THREAT_ANALYSIS_OPTIONS = [
  {id: 1, content: 'Threat detected'}, {id: 2, content: 'Undetermined'}, {id: 3, content: 'No threat'}, {id: 4, content: 'Analysing'},
];

export const getDefaultSimilarMessagesState = (): SimilarMessagesState => ({
  similarity: [],
  status: [],
  priority: [],
  category: [],
  threat_analysis: [],
  keyword: '',
  ...getSimilarWindow(),
  page: 1,
  perPages: 10,
  // CURRENT MESSAGE, Same Sender & Subject, Same Sender, Same Subject; priority high to low inside each group
  sort: 'asc',
  sortType: 'similarity',
});

const buildDescription = (similarity: string[], criteria: {sender: string, subject: string}) => {
  const sender = <b>{criteria.sender}</b>;
  const subject = <b>{criteria.subject}</b>;
  const chosen = SIMILARITY_OPTIONS.filter(({content}) => similarity.includes(content)).map(({key}) => key);
  if (chosen.length === 1 && chosen[0] === 'same_sender') {
    return <>Messages matching the same sender ({sender}).</>;
  }
  if (chosen.length === 1 && chosen[0] === 'same_subject') {
    return <>Messages in this campaign which has the same subject {subject}</>;
  }
  return <>Messages matching the same sender ({sender}), the same subject ({subject}), or <b>both</b></>;
};

export const SimilarMessages: React.FC<SimilarMessagesProps> = ({state, setState, ...props}) => {
  useEffect(() => {
    if (!state) {
      setState(getDefaultSimilarMessagesState());
    }
  }, [state, setState]);

  return state ? <SimilarMessagesContent {...props} state={state} setState={setState as SimilarMessagesContentProps['setState']}/> : null;
};

const SimilarMessagesContent: React.FC<SimilarMessagesContentProps> = ({emailId, state, setState, onCurrentMessageUpdated}) => {
  const tzName = getTimeZoneName();
  const [rows, setRows] = useState<any[]>([]);
  const [totals, setTotals] = useState({totalItems: 0, maxPage: 0, start: 0, end: 0});
  const [criteria, setCriteria] = useState({sender: '', subject: ''});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const [searchValue, setSearchValue] = useState(state.keyword);
  const [filtersOpen, setFiltersOpen] = useState<any>({});
  const [rowSelected, setRowSelected] = useState<any[]>([]);
  const [rowMenu, setRowMenu] = useState<{anchor: HTMLElement, emailId: string} | null>(null);
  const [bulkTargets, setBulkTargets] = useState<string[]>([]);
  const [bulkApplying, setBulkApplying] = useState(false);
  const [toast, setToast] = useState({type: '', message: '', isOpen: false, toastId: 0});
  const requestId = useRef(0);

  useEffect(() => {
    const current = ++requestId.current;
    setLoading(true);
    setError('');
    getSimilarMessages({
      emailId,
      startDate: state.startDate,
      endDate: state.endDate,
      similarity: SIMILARITY_OPTIONS.filter(({content}) => state.similarity.includes(content)).map(({key}) => key),
      status: state.status,
      priority: state.priority,
      category: state.category,
      threat_analysis: state.threat_analysis,
      keyword: state.keyword,
      page: state.page,
      perPages: state.perPages,
      sort: state.sort,
      sortType: state.sortType,
    }).then((result: any) => {
      if (current !== requestId.current) {
        return;
      }
      const data = result.data || {};
      setRows(data.list || []);
      setTotals({
        totalItems: data.totalItems || 0,
        maxPage: data.maxPage || 0,
        start: data.startItemsPerPage || 0,
        end: data.endItemsPerPage || 0,
      });
      setCriteria(data.criteria || {sender: '', subject: ''});
      setLoading(false);
    }).catch(() => {
      if (current !== requestId.current) {
        return;
      }
      setRows([]);
      setTotals({totalItems: 0, maxPage: 0, start: 0, end: 0});
      setError('Unable to load similar messages. Please try again.');
      setLoading(false);
    });
  }, [emailId, state, reloadKey]);

  const update = (changes: Partial<SimilarMessagesState>) => setState((prev) => ({...prev, page: 1, ...changes}));

  const selectedItems = {
    similarity: state.similarity,
    status: state.status,
    priority: state.priority,
    category: state.category,
    threat_analysis: state.threat_analysis,
  };

  const handleFilter = (items: any, type: string) => update({[type]: items} as any);

  const handleClearFilter = (type: string) => update({[type]: []} as any);

  const handleReset = () => {
    const defaults = getDefaultSimilarMessagesState();
    setSearchValue('');
    setRowSelected([]);
    setState({...defaults, startDate: state.startDate, endDate: state.endDate, perPages: state.perPages});
  };

  const handleEnterSearch = (event: any) => {
    if (event.keyCode === 13) {
      update({keyword: event.target.value});
    }
  };

  const handleSort = (model: any) => {
    if (model && model.length > 0) {
      update({sort: model[0].sort, sortType: model[0].field});
    }
  };

  const openBulkAction = (targets: string[]) => {
    const emailIds = targets.filter(Boolean).map((id) => id.toString());
    if (emailIds.length === 0) {
      return;
    }
    setRowMenu(null);
    setBulkTargets(emailIds);
  };

  const closeBulkAction = () => {
    if (!bulkApplying) {
      setBulkTargets([]);
    }
  };

  const showToast = (type: string, message: string) => setToast((prev) => ({type, message, isOpen: true, toastId: prev.toastId + 1}));

  const applyBulkAction = (changes: BulkActionChanges) => {
    const targets = bulkTargets;
    setBulkApplying(true);
    const finish = (response: any) => {
      const outcome = getBulkActionOutcome(targets, response);
      setBulkApplying(false);
      setBulkTargets([]);
      setRowSelected(outcome.failedIds);
      showToast(outcome.type, outcome.message);
      setReloadKey((key) => key + 1);
      if (targets.includes(emailId) && !outcome.failedIds.includes(emailId)) {
        onCurrentMessageUpdated?.(changes);
      }
    };
    return updateTriageInboxes({emailIds: targets, ...changes})
      .then((result: any) => finish(result.data || {}))
      .catch(() => finish({
        failed: targets.map((email_id) => ({email_id, code: 'UPDATE_FAILED', message: 'Inbox message could not be updated'})),
      }));
  };

  const columns: GridColDef[] = [
    {
      field: 'priority', headerName: 'Priority', width: 120, sortable: true,
      renderCell: (row) => <TableDropdown emailId={row.id} title={row.formattedValue} variant='priority'
        background={priorityDropdown[row.formattedValue]?.bg} backgroundHover={priorityDropdown[row.formattedValue]?.hover}
        borderRadius={100}/>
    },
    {
      field: 'threat_score', headerName: 'Threat Analysis', width: 160, sortable: false,
      renderCell: (row) => row.formattedValue === -1
        ? <Tooltip placement={'top'} title={'Scanning in progress. No threat score yet.'}><span>ANALYSING...</span></Tooltip>
        : <RadialGraph value={row.formattedValue} isToolTipHover={true} chartStyle={{stroke: 'unset'}}/>
    },
    {
      field: 'status', headerName: 'Process Status', width: 120, sortable: true,
      renderCell: (row) => <TableDropdown emailId={row.id} title={row.formattedValue} variant='status' color='#212121'
        background={statusDropdown[row.formattedValue]?.bg} backgroundHover={statusDropdown[row.formattedValue]?.hover}
        borderRadius={2} padding={'4px 4px 4px 8px'}/>
    },
    {
      field: 'category', headerName: 'Category', width: 120, sortable: true,
      renderCell: (row) => <TableDropdown emailId={row.id} title={row.formattedValue} variant='category' color='#212121'
        background='transparent' backgroundHover='transparent' borderRadius={100}/>
    },
    {
      field: 'similarity', headerName: 'Similarity', width: 200, sortable: true,
      renderCell: (row) => row.formattedValue === 'current'
        ? <span className={'similar-current'} data-testid={'similar-current-message'}>{SIMILARITY_LABELS.current}</span>
        : <span>{SIMILARITY_LABELS[row.formattedValue] || ''}</span>
    },
    {
      field: 'email_from', headerName: 'From (Email)', width: 260, sortable: false,
      renderCell: (row) => <Tooltip placement={'top'} title={row.formattedValue}>
        <span className={'MuiDataGrid-cellContent'}>{row.formattedValue}</span>
      </Tooltip>
    },
    {
      field: 'subject', headerName: 'Subject', width: 300, sortable: true,
      renderCell: (row) => <Tooltip placement={'top'} title={row.formattedValue}>
        <span className={'MuiDataGrid-cellContent'}>{row.formattedValue}</span>
      </Tooltip>
    },
    {
      field: 'reported_at', headerName: 'Reported At', width: 120, sortable: true,
      renderCell: (row) => <div>
        <span>{moment.utc(row.formattedValue, 'DD/MM/YYYY hh:mm:ss').tz(tzName).format('DD/MM/YYYY')}</span>
        <br/>
        <span>{moment.utc(row.formattedValue, 'DD/MM/YYYY hh:mm:ss').tz(tzName).format('hh:mm A')}</span>
      </div>
    },
    {
      field: 'reported_by', headerName: 'Reported By (Email)', width: 220, sortable: false,
      renderCell: (row) => <Tooltip placement={'top'} title={row.formattedValue}>
        <span className={'MuiDataGrid-cellContent'}>{row.formattedValue}</span>
      </Tooltip>
    },
    {
      field: 'actions', headerName: 'Actions', width: 100, sortable: false,
      renderCell: (row) => <IconButton
        aria-label={'Row actions'}
        data-testid={'similar-row-actions'}
        onClick={(event) => setRowMenu({anchor: event.currentTarget, emailId: row.id.toString()})}
      ><MoreHorizIcon/></IconButton>
    },
  ];

  const paginationComponent = () => <Pagination
    itemsPerPage={state.perPages}
    currentPage={state.page}
    startPoint={totals.start}
    endPoint={totals.end}
    maxPage={totals.maxPage}
    totalItems={totals.totalItems}
    handlePerRowsChange={(event: any) => update({perPages: parseInt(event.target.value)})}
    handlePageChange={(page: number) => setState((prev) => ({...prev, page}))}
  />;

  // same Actions dropdown and column button as the Inbox list
  const toolbar = () => <div className={'toolbar similar-toolbar'}>
    <div className={'left-toolbar'}>
      {rowSelected.length > 0 && <div className={'rows-selected'}>
        <IconButton
          size={'small'}
          aria-label={'Clear selection'}
          data-testid={'similar-clear-selection'}
          onClick={() => setRowSelected([])}
        ><CloseIcon fontSize={'small'}/></IconButton>
        <span className={'similar-selected'}>{rowSelected.length} Selected</span>
      </div>}
      <DropDownList
        width={124}
        height={36}
        border={'none'}
        variantDropdown={'actions-dropdown-inbox'}
        selectedTitle={'Actions'}
        disabled={rowSelected.length === 0}
        children={<DropdownContent
          inboxActionMenu
          menuData={rowSelected}
          actionOtherData={BULK_ACTION_MENU}
          handleBulkAction={openBulkAction}
        />}
      />
    </div>
    <div className={'right-toolbar'}>
      <GridToolbarColumnsButton {...({} as any)}/>
    </div>
  </div>;

  return <div className={'similar-messages'} data-testid={'similar-messages'}>
    <div className={'similar-header'}>
      <div>
        <h2 className={'similar-title'}>Messages List</h2>
        <p className={'similar-description'} data-testid={'similar-description'}>
          {buildDescription(state.similarity, criteria)}
        </p>
      </div>
      <DateRangePicker
        start={state.startDate}
        end={state.endDate}
        dates={{startDate: state.startDate, endDate: state.endDate}}
        titleCalendar={'Similar Email Count : '}
        cssClass={'daterange-input'}
        maxSpan={{'years': 2}}
        handleApplyDateRange={({startDate, endDate}: any) => update({startDate, endDate})}
      />
    </div>
    <div className={'similar-filters'}>
      <SearchMenuBar
        selectedItems={selectedItems}
        resetFilter={handleReset}
        handleEnterSearch={handleEnterSearch}
        onFilter={handleFilter}
        searchInputValue={searchValue}
        setSearchInputValue={setSearchValue}
        onClearFilter={handleClearFilter}
        filtersOpen={filtersOpen}
        setFiltersOpen={setFiltersOpen}
        dropdownFilters={[
          {label: 'Similarity', type: 'similarity', arrList: SIMILARITY_OPTIONS, width: 125},
          {label: 'Process Status', type: 'status', arrList: STATUS_OPTIONS, width: 150},
          {label: 'Priority', type: 'priority', arrList: PRIORITY_OPTIONS, width: 95},
          {label: 'Category', type: 'category', arrList: CATEGORY_OPTIONS, width: 107},
          {label: 'Threat Analysis', type: 'threat_analysis', arrList: THREAT_ANALYSIS_OPTIONS, width: 150},
        ]}
      />
    </div>
    {error && <Box role={'alert'} sx={{color: '#B20007', mb: 2}}>{error}</Box>}
    <div className={'similar-datatable'}>
      <DataTable
        columns={columns}
        data={rows}
        rowCount={totals.totalItems}
        filterData={{}}
        onSelectionModelChange={setRowSelected}
        selectionModel={rowSelected}
        CustomPagination={paginationComponent}
        toolbar={toolbar}
        onSortModelChange={handleSort}
        loadingTable={loading}
        checkboxSelection={true}
        pagination={true}
        initialSortCol={state.sortType}
        initialSortDir={state.sort}
        getRowClassName={(params: any) => params.row.similarity === 'current' ? 'similar-row-current' : ''}
      />
    </div>
    <Menu
      id={'actions-customized-menu'}
      className={'inbox-actions-menu'}
      anchorEl={rowMenu?.anchor}
      open={Boolean(rowMenu)}
      onClose={() => setRowMenu(null)}
      anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
      transformOrigin={{vertical: 'top', horizontal: 'right'}}
    >
      <DropdownContent
        inboxActionMenu
        menuData={rowMenu ? [rowMenu.emailId] : []}
        actionOtherData={BULK_ACTION_MENU}
        handleBulkAction={openBulkAction}
      />
    </Menu>
    <BulkActionModal
      open={bulkTargets.length > 0}
      applying={bulkApplying}
      selectedCount={bulkTargets.length}
      onCancel={closeBulkAction}
      onApply={applyBulkAction}
    />
    <Toast isOpen={toast.isOpen} type={toast.type} message={toast.message} toastId={toast.toastId} autoHideDuration={2000}/>
  </div>;
};
